import React, { useState } from 'react';
import css from './ConfigListing.module.css';
import SectionCard from './SectionCard';
import AvatarUploadField from './fields/avatar-upload';
import { Form as FinalForm } from 'react-final-form';
import { FormattedMessage, useIntl } from 'react-intl';
import * as validators from '../../util/validators';
import {
  FieldCheckboxGroup,
  FieldCurrencyInput,
  FieldNumber,
  FieldTextInput,
  PrimaryButton,
} from '../../components';
import arrayMutators from 'final-form-arrays';
import appSettings from '../../config/settings';
import { useConfiguration } from '../../context/configurationContext';
import { ensureDotSeparator } from '../../util/currency';
import { getPrice } from '../../components/FieldCurrencyInput/FieldCurrencyInput';
import EditListingPhotosForm from '../EditListingPage/EditListingWizard/EditListingPhotosPanel/EditListingPhotosForm';
import { Card } from '../../components/Card/Card';
import { useListingManager } from './hooks/useListingManager';
import { useUpdatePersonalData } from './hooks/useUpdatePersonalData';

const DisplayNameMaybe = props => {
  const { intl, disabled } = props;

  const displayNameRequired = validators.required(
    intl.formatMessage({
      id: 'ProfileSettingsForm.displayNameRequired',
    })
  );

  return (
    <div className={css.sectionContainer}>
      <FieldTextInput
        className={css.row}
        type="text"
        id="displayName"
        name="displayName"
        label={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNameLabel',
        })}
        placeholder={intl.formatMessage({
          id: 'ProfileSettingsForm.displayNamePlaceholder',
        })}
        validate={displayNameRequired}
        disabled={disabled}
      />
    </div>
  );
};

const parseOptionsForFieldInConfig = (key, config) => {
  return (
    config?.listing?.listingFields?.find?.(field => field.key === key) || []
  )?.enumOptions?.map?.(option => ({
    key: option.option,
    label: option.label,
  }));
};

const successMessage = setMessage => {
  setMessage({
    type: 'success',
    message: 'Profile updated successfully',
  });
  setTimeout(() => {
    setMessage(null);
  }, 3000);
};

const ConfigListing = ({ user: storeUser }) => {
  const [userFetched, setUserFetched] = useState(storeUser);
  const user = userFetched;
  const [profileMessage, setProfileMessage] = useState(null);
  const { onUpdateProfile, isLoading: profileIsLoading } = useUpdatePersonalData();
  const { listing, isLoading, imagesFetched } = useListingManager(user?.id?.uuid);
  const config = useConfiguration();
  const firstNameRequired = validators.required('First name is required');
  const lastNameRequired = validators.required('Last name is required');
  const priceMin = priceMax => validators.numerAtMax('Shoul be lower ten price max', priceMax);
  const priceMax = priceMin => validators.numberAtLeast('Shoul be greater ten price min', priceMin);
  const intl = useIntl();
  const backgroundCheck = user?.attributes?.profile?.publicData?.background_check === 'yes';

  const listingData = listing?.attributes || {};
  const currencyConfig = appSettings.getCurrencyFormatting(config?.currency);

  const servicesOptions = parseOptionsForFieldInConfig('services', config);
  const careOptions = parseOptionsForFieldInConfig('care_settings', config);
  const languagesOptions = parseOptionsForFieldInConfig('languages', config);
  const specialitiesOptions = parseOptionsForFieldInConfig('specialties_and_experience', config);

  const handleUpdateListing = async values => {};
  const handleProfileUpdate = async values => {
    try {
      const result = await onUpdateProfile({
        displayName: values.displayName,
        firstName: values.firstName,
        lastName: values.lastName,
        publicData: {
          background_check: values.backgroundCheck ? 'yes' : 'no',
        },
      });
      setUserFetched(result);
      successMessage(setProfileMessage);
    } catch (error) {}
  };

  return (
    <div className={css.wrapper}>
      {!isLoading && (
        <FinalForm
          mutators={{ ...arrayMutators }}
          onSubmit={handleUpdateListing}
          initialValues={{
            firstName: user?.attributes?.profile?.firstName,
            lastName: user?.attributes?.profile?.lastName,
            displayName: user?.attributes?.profile?.displayName,
            backgroundCheck: backgroundCheck,
            description: listingData?.description,
            price_min: listingData?.publicData?.price_min
              ? getPrice(
                  ensureDotSeparator(listingData?.publicData?.price_min.toString()),
                  currencyConfig
                )
              : undefined,
            price_max: listingData?.publicData?.price_max
              ? getPrice(
                  ensureDotSeparator(listingData?.publicData?.price_max.toString()),
                  currencyConfig
                )
              : undefined,
            price_description: listingData?.publicData?.price_description,
            years_of_experience: listingData?.publicData?.years_of_experience,
            services: listingData?.publicData?.services,
            care_settings: listingData?.publicData?.care_settings,
            languages: listingData?.publicData?.languages,
            specialties_and_experience: listingData?.publicData?.specialties_and_experience,
            credentials_description: listingData?.publicData?.credentials_description,
          }}
        >
          {({ form, handleSubmit }) => (
            <>
              {console.log(profileIsLoading)}
              <div className={css.mainColumn}>
                <SectionCard title={'Profile image'}>
                  <div className={css.center}>
                    <AvatarUploadField user={user} form={form} />
                    <div className={css.imageContentWrapper}>
                      <div className={css.tip}>
                        <FormattedMessage id="ProfileSettingsForm.tip" />
                      </div>
                      <div className={css.fileInfo}>
                        <FormattedMessage id="ProfileSettingsForm.fileInfo" />
                      </div>
                    </div>
                  </div>
                </SectionCard>
                <SectionCard title={'Personal information'}>
                  <div className={css.sectionContainer}>
                    <div className={css.nameContainer}>
                      <FieldTextInput
                        className={css.firstName}
                        type="text"
                        id="firstName"
                        name="firstName"
                        label={'First name'}
                        placeholder={'First name'}
                        validate={firstNameRequired}
                        disabled={profileIsLoading}
                      />
                      <FieldTextInput
                        className={css.lastName}
                        type="text"
                        id="lastName"
                        name="lastName"
                        label={'Last name'}
                        placeholder={'Last name'}
                        validate={lastNameRequired}
                        disabled={profileIsLoading}
                      />
                    </div>
                    <DisplayNameMaybe intl={intl} />
                    <FieldCheckboxGroup
                      name="backgroundCheck"
                      id={'backgroundCheck'}
                      optionLabelClassName={css.finePrint}
                      twoColumns={false}
                      options={[
                        {
                          key: 'backgroundCheck',
                          label: 'Do you have a background check?',
                        },
                      ]}
                      disabled={profileIsLoading}
                    />
                    <PrimaryButton
                      style={{
                        marginTop: '24px',
                      }}
                      onClick={() => {
                        handleProfileUpdate(form?.getState()?.values);
                      }}
                      disabled={profileIsLoading}
                      isLoading={profileIsLoading}
                    >
                      Save
                    </PrimaryButton>
                    {profileMessage && (
                      <Card>
                        <div
                          style={{ padding: '16px', textAlign: 'center', background: 'lightgreen' }}
                        >
                          <FormattedMessage
                            id={profileMessage?.message}
                            defaultMessage={profileMessage?.message}
                          />
                        </div>
                      </Card>
                    )}
                  </div>
                </SectionCard>
                <SectionCard title={'Profile information'}>
                  <FieldTextInput
                    className={css.description}
                    type="textarea"
                    id="description"
                    name="description"
                    label={'Description'}
                    placeholder={'Description'}
                  />
                  <div className={css.nameContainer}>
                    <FieldCurrencyInput
                      className={css.firstName}
                      type="number"
                      id="price_min"
                      name="price_min"
                      label={'Price min'}
                      placeholder={intl.formatMessage({
                        id: 'Cappa.validate.minPrice',
                      })}
                      currencyConfig={currencyConfig}
                      validate={value => {
                        return priceMin(
                          form?.getState()?.values?.price_max?.amount / 100 || Infinity
                        )(value?.amount / 100);
                      }}
                    />
                    <FieldCurrencyInput
                      className={css.firstName}
                      type="number"
                      id="price_max"
                      name="price_max"
                      label={'Price max'}
                      placeholder={intl.formatMessage({
                        id: 'Cappa.validate.maxPrice',
                      })}
                      currencyConfig={currencyConfig}
                      validate={value => {
                        return priceMax(
                          form?.getState()?.values?.price_min?.amount / 100 || -Infinity
                        )(value?.amount / 100);
                      }}
                    />
                  </div>
                  <FieldTextInput
                    className={css.description}
                    type="textarea"
                    id="price_description"
                    name="price_description"
                    label={'Price description'}
                    placeholder={'Price description'}
                  />
                  <div
                    style={{
                      width: 'min-content',
                    }}
                  >
                    <FieldNumber
                      className={css.description}
                      type="number"
                      id="years_of_experience"
                      name="years_of_experience"
                      label={'Years of experience'}
                      placeholder={'Years of experience'}
                      validate={validators.numberAtLeast(
                        intl.formatMessage({
                          id: 'Cappa.validate.yearsOfExperience',
                        }),
                        -1
                      )}
                    />
                  </div>
                  <FieldCheckboxGroup
                    schemaType="multi-enum"
                    id="services"
                    name="services"
                    label={'Services'}
                    options={servicesOptions}
                  />
                  <FieldCheckboxGroup
                    schemaType="multi-enum"
                    id="care_settings"
                    name="care_settings"
                    label={'Care settings'}
                    options={careOptions}
                  />
                  <FieldCheckboxGroup
                    schemaType="multi-enum"
                    id="languages"
                    name="languages"
                    label={'Languages'}
                    options={languagesOptions}
                  />
                  <FieldCheckboxGroup
                    schemaType="multi-enum"
                    id="specialties_and_experience"
                    name="specialties_and_experience"
                    label={'Specialties and experience'}
                    options={specialitiesOptions}
                  />
                  <FieldTextInput
                    className={css.description}
                    type="textarea"
                    id="credentials_description"
                    name="credentials_description"
                    label={'Credentials description'}
                    placeholder={'Credentials description'}
                  />
                </SectionCard>
                <SectionCard title={'Availability'}>
                  <EditListingPhotosForm
                    className={css.form}
                    disabled={false}
                    ready={true}
                    fetchErrors={{
                      createListingDraftError: null,
                      publishListingError: null,
                      updateListingError: null,
                      showListingsError: null,
                      uploadImageError: null,
                      setStockError: null,
                      createStripeAccountError: null,
                      addExceptionError: null,
                      deleteExceptionError: null,
                    }}
                    initialValues={{
                      images: imagesFetched || [],
                    }}
                    onImageUpload={() => {}}
                    onSubmit={values => {
                      const { addImage, ...updateValues } = values;
                      console.log(updateValues);
                    }}
                    onRemoveImage={() => {}}
                    saveActionMsg={'Test'}
                    updated={false}
                    updateInProgress={false}
                    listingImageConfig={config.layout.listingImage || {}}
                  />
                </SectionCard>
                <PrimaryButton
                  type="submit"
                  onClick={() => {
                    handleSubmit(handleUpdateListing);
                  }}
                >
                  Save
                </PrimaryButton>
              </div>
              <div className={css.rightColumn}>
                <div
                  style={{
                    position: 'sticky',
                    top: 108,
                  }}
                >
                  <Card>Listing configuration</Card>
                </div>
              </div>
            </>
          )}
        </FinalForm>
      )}
    </div>
  );
};

export default ConfigListing;
